import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { useResponsive } from '../../hooks/useResponsive';
import { ImpersonationMode } from '../../types/Impersonation';

const isWeb = Platform.OS === 'web';

/**
 * Height of the banner in pixels (single-row). Used by Sidebar to offset below it.
 * Sidebar only renders on wide web viewports where the banner is always single-row.
 */
export const IMPERSONATION_BANNER_HEIGHT = isWeb ? 37 : 50;

const formatTime = (seconds: number): string => {
  const clamped = Math.max(0, seconds);
  const mins = Math.floor(clamped / 60);
  const secs = clamped % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const formatModeLabel = (mode: ImpersonationMode): string => {
  switch (mode) {
    case 'full_access':
      return 'Full Access';
    case 'read_only':
      return 'Read Only';
  }
};

export const ImpersonationBanner: React.FC = () => {
  const {
    isImpersonating,
    targetUser,
    mode,
    timeRemaining,
    endImpersonation,
    isLoading,
    error,
  } = useImpersonation();
  const { isSmallScreen } = useResponsive();

  if (!isImpersonating) {
    return null;
  }

  // Two-row on native always; on web when viewport is narrow
  const compact = !isWeb || isSmallScreen;

  const displayName = targetUser?.name ?? 'Unknown User';
  const displayMode = mode ? formatModeLabel(mode) : null;
  const displayTime = formatTime(timeRemaining ?? 0);

  const endButton = (
    <TouchableOpacity
      style={[styles.endButton, isLoading && styles.endButtonDisabled]}
      onPress={endImpersonation}
      disabled={isLoading}
      accessibilityLabel="End impersonation session"
      accessibilityRole="button"
      testID="impersonation-end-session"
    >
      {isLoading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color="#ffffff" />
          <Text style={styles.endButtonText}>Ending...</Text>
        </View>
      ) : (
        <Text style={styles.endButtonText}>End Session</Text>
      )}
    </TouchableOpacity>
  );

  if (compact) {
    // Two-row layout: native always, web when narrow
    return (
      <View
        style={[styles.bannerBase, styles.bannerCompact]}
        accessibilityRole="summary"
        testID="impersonation-banner"
        accessibilityLabel={`Viewing as ${displayName}${displayMode ? ` in ${displayMode} mode` : ''}. ${displayTime} remaining.`}
      >
        <View style={styles.compactRow}>
          <Text style={[styles.bannerText, styles.bannerTextCompact]} numberOfLines={1} testID="impersonation-banner-text">
            Impersonating: {displayName}
          </Text>
          {displayMode && (
            <View style={styles.modeBadge} testID="impersonation-mode-badge">
              <Text style={styles.modeText}>{displayMode}</Text>
            </View>
          )}
        </View>
        <View style={styles.compactRow}>
          <Text style={styles.timerText} testID="impersonation-timer">{displayTime} remaining</Text>
          {error && (
            <Text style={styles.errorText} numberOfLines={1} accessibilityRole="alert">
              {error}
            </Text>
          )}
          {endButton}
        </View>
      </View>
    );
  }

  // Wide web: single-row layout
  return (
    <View
      style={[styles.bannerBase, styles.bannerWide]}
      accessibilityRole="summary"
      testID="impersonation-banner"
      accessibilityLabel={`Viewing as ${displayName}${displayMode ? ` in ${displayMode} mode` : ''}. ${displayTime} remaining.`}
    >
      <View style={styles.infoSection}>
        <Text style={styles.bannerText} testID="impersonation-banner-text">Impersonating: {displayName}</Text>
        {displayMode && (
          <View style={styles.modeBadge} testID="impersonation-mode-badge">
            <Text style={styles.modeText}>{displayMode}</Text>
          </View>
        )}
        <Text style={styles.timerText} testID="impersonation-timer">{displayTime}</Text>
      </View>
      {error && (
        <Text style={styles.errorText} accessibilityRole="alert">
          {error}
        </Text>
      )}
      {endButton}
    </View>
  );
};

const styles = StyleSheet.create({
  bannerBase: {
    backgroundColor: '#fff3cd',
    borderBottomWidth: 1,
    borderBottomColor: '#ffc107',
    paddingHorizontal: 16,
    width: '100%',
    ...Platform.select({
      web: {
        zIndex: 1000,
      },
      default: {},
    }),
  },
  bannerWide: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  bannerCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    paddingVertical: 4,
    gap: 2,
  },
  compactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  infoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  bannerText: {
    color: '#856404',
    fontSize: 14,
    fontWeight: '600',
    flexShrink: 1,
  },
  bannerTextCompact: {
    fontSize: 13,
  },
  modeBadge: {
    backgroundColor: '#ffc107',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  modeText: {
    color: '#856404',
    fontSize: 12,
    fontWeight: '600',
  },
  timerText: {
    color: '#856404',
    fontSize: 14,
    fontWeight: '500',
    fontVariant: ['tabular-nums'],
  },
  errorText: {
    color: '#dc3545',
    fontSize: 12,
    fontWeight: '500',
    marginRight: 8,
    flexShrink: 1,
  },
  endButton: {
    backgroundColor: '#dc3545',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  endButtonDisabled: {
    opacity: 0.7,
  },
  endButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
});
